<template>
	<div class="page-login">
		<div class="container">
			<!-- Login Box -->
			<div class="login-box d-flex align-items-center">
				<div class="login-box-image">
					<img src="img/login-logo.png" class="d-block m-auto" alt="MyMeet">
				</div>
				<div class="login-box-content">
					<template v-if="!isShowResetPassword && !isShowLoginLink">
						<div class="mb-1 clearfix">
							<div class="float-left">
								<h4>{{ $t('login') }}</h4>
							</div>
						</div>
						<div class="login-error callout callout--danger" v-if="loginError">
							<div class="callout__content">{{ loginError }}</div>
						</div>
						<form @submit.prevent="checkLogin({ email, password })">
							<md-field>
								<md-icon>email</md-icon>
								<label>{{ $t('email') }}</label>
								<md-input name="email" v-model="email" ref="email" data-test="login_input_email" id="login_input_email"></md-input>
							</md-field>
							<md-field>
								<md-icon>lock</md-icon>
								<label>{{ $t('password') }}</label>
								<md-input type="password" name="password" v-model="password" autocomplete="on" data-test="login_input_password" id="login_input_password"></md-input>
							</md-field>
							<div>
								<button
									type="submit"
									class="btn btn-submit btn-primary btn-block btn-lg raised"
									:class="{ 'is-loading': pending }"
									data-test="login_button_login"
									id="login_button_login">
									{{ $t('signIn') }}
								</button>
							</div>

							<div class="d-flex justify-content-between align-items-center mt-4">
								<div class="text-right">
									<a
										class="login-action"
										@click="showResetPassword"
										data-test="login_button_forgot_password"
										id="login_button_forgot_password">
										{{ $t('forgotPassword') }}?
									</a>
									<span> {{ $t('or') }} </span>
									<a
										class="login-action"
										@click="showLinkLogin"
										data-test="login_button_login_link"
										id="login_button_login_link">
										{{ $t('Login with link') }}?
									</a>
								</div>
							</div>
						</form>
					</template>
					<!-- Reset Password -->
					<template v-if="isShowResetPassword">
						<div class="mb-1 clearfix">
							<div class="float-left">
								<h4>{{ $t('forgotPassword') }}</h4>
							</div>
						</div>
						<div class="login-error callout callout--danger" v-if="resetPasswordError">
							<div class="callout__content">{{ resetPasswordError }}</div>
						</div>
						<div class="login-error callout callout--success" v-if="resetPasswordSuccess">
							<div class="callout__title">{{ $t('pleaseCheckYourEmail') }}</div>
							<div class="callout__content">{{ $t('ifYourEmailIsRegisteredInMyMeetYouWillReceiveLink') }}</div>
						</div>
						<form @submit.prevent="resetPassword({ email, password })">
							<div class="form-group">
								<md-field>
									<md-icon>email</md-icon>
									<label>{{ $t('email') }}</label>
									<md-input name="email" v-model="email" id="reset_input_email"></md-input>
								</md-field>
							</div>
							<button type="submit" class="btn btn-lg btn-submit btn-primary btn-block raised" id="reset_button_submit" :class="{ 'is-loading': isResettingPassword }">
								{{ $t('resetPassword') }}
							</button>
						</form>
						<div class="text-right mt-4">
							<a class="login-action" @click="showLogin">{{ $t('backToLogin') }}</a>
						</div>
					</template>

					<!-- Login with link -->
					<template v-if="isShowLoginLink">
						<div class="mb-1 clearfix">
							<div class="float-left">
								<h4>{{ $t('Login with link') }}</h4>
							</div>
						</div>
						<div class="login-error callout callout--danger" v-if="loginLinkError">
							<div class="callout__content">{{ $t('Email is not registered, please check your email or contact admin') }}</div>
						</div>
						<div class="login-error callout callout--success" v-if="loginLinkSuccess">
							<div class="callout__title">{{ $t('pleaseCheckYourEmail') }}</div>
						</div>
						<form @submit.prevent="loginLink({ email })">
							<div class="form-group">
								<md-field>
									<md-icon>email</md-icon>
									<label>{{ $t('email') }}</label>
									<md-input name="email" v-model="email" id="login_link_input_email"></md-input>
								</md-field>
							</div>
							<button type="submit" class="btn btn-lg btn-submit btn-primary btn-block raised" id="login_link_button_submit" :class="{ 'is-loading': isLoginLinkSubmit }">
								{{ $t('submit') }}
							</button>
						</form>
						<div class="text-right mt-4">
							<a class="login-action" @click="showLogin">{{ $t('backToLogin') }}</a>
						</div>
					</template>
				</div>
			</div>
		</div>
		<div class="modals">

			<!-- Modal force logout -->
			<modal class="modal-remove" size="medium" v-if="isShowModalForceLogout" @close="closeModalForceLogout">
				<div slot="header">
					<h5 class="modal-title">
						{{ $t('You are moving the login') }}
					</h5>
				</div>
				<div slot="body">
					{{ $t("You were previously logged in to MyMeet through a different PC or browser. We are now redirecting your MyMeet login to this browser. That's all right, so keep on.") }}
				</div>
				<div slot="footer">
					<div class="modal-actions clearfix">
						<button type="button" class="btn btn-lg raised btn-secondary btn-save" :class="{ 'is-loading': pending }" @click="closeModalForceLogout">
							{{ $t('cancel') }}
						</button>
						<button type="button" class="btn btn-lg raised btn-danger btn-save" :class="{ 'is-loading': pending }" @click="login">
							{{ $t('yes') }}
						</button>
					</div>
				</div>
			</modal>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userApi from '../api/user';

const Modal = () => import('@/components/Modal.vue');

export default {
	name: 'login',
	components: {
		Modal,
	},
	data() {
		return {
			isShowResetPassword: false,
			isResettingPassword: false,
			resetPasswordError: false,
			resetPasswordSuccess: false,
			isShowModalForceLogout: false,
			title: 'Login',
			email: '',
			password: '',
			isShowLoginLink: false,
			loginLinkError: false,
			loginLinkSuccess: false,
			isLoginLinkSubmit: false,
		};
	},
	mounted() {
		this.focusInputEmail();
	},
	methods: {
		...mapActions({
		}),
		showModalForceLogout() {
			this.isShowModalForceLogout = true;
		},
		closeModalForceLogout() {
			this.isShowModalForceLogout = false;
		},
		checkLogin() {
			this.$store.dispatch('loginProcess');
			userApi.checkLogin({
				email: this.email,
				password: this.password,
			}).then(() => {
				// const isAnotherLogin = response.data.is_another_login;
				// const isSpectator = response.data.is_spectator;
				// if (isAnotherLogin && !isSpectator) {
				// 	this.showModalForceLogout();
				// 	this.$store.dispatch('loginFailed');
				// } else {
				// 	this.login();
				// }

				this.login();
			}).catch((error) => {
				this.$store.dispatch('loginFailed');
				const message = error.response.data.message;
				this.$notify({
					group: 'foo-velocity',
					title: this.$t('error'),
					text: message,
					type: 'error',
				});
			});
		},
		login() {
			this.$store.dispatch('loginProcess');
			this.loginLocal();
		},
		loginLocal() {
			userApi.login({
				email: this.email,
				password: this.password,
			}).then((response) => {
				const token = response.data.data;
				const data = {
					store: this.$store,
					token,
				};
				this.$store.dispatch('loginSuccessLocal', data);
			}).catch((error) => {
				const message = error.response.data.message;
				this.$notify({
					group: 'foo-velocity',
					title: this.$t('error'),
					text: message,
					type: 'error',
				});
				this.$store.dispatch('loginFailed');
			});
		},
		resetPassword() {
			this.isResettingPassword = true;
			const params = {
				email: this.email,
				language: localStorage.getItem('language') || 'no',
			};
			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'foo-velocity',
					// title: this.$t('success'),
					text: message,
					type: 'success',
				});
				this.resetPasswordSuccess = true;
				this.isResettingPassword = false;
			};
			const errorCallback = (error) => {
				this.isResettingPassword = false;

				const message = error.response.data.message;
				this.$notify({
					group: 'foo-velocity',
					title: this.$t('error'),
					text: message,
					type: 'error',
				});
			};
			userApi.resetPassword(params, callback, errorCallback);
		},
		showResetPassword(event) {
			event.preventDefault();
			this.isShowResetPassword = true;
			this.isShowLoginLink = false;
		},
		showLogin() {
			this.isShowResetPassword = false;
			this.isShowLoginLink = false;
		},
		focusInputEmail() {
			if (this.$refs.email) this.$refs.email.$el.focus();
		},
		showLinkLogin(event) {
			event.preventDefault();
			this.isShowResetPassword = false;
			this.isShowLoginLink = true;
		},
		loginLink() {
			if (!this.email) return;
			const params = {
				email: this.email,
			};
			this.isLoginLinkSubmit = true;
			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'foo-velocity',
					// title: this.$t('success'),
					text: message,
					type: 'success',
				});
				this.isLoginLinkSubmit = false;
				this.loginLinkSuccess = true;
				this.loginLinkError = false;
			};
			const errorCallback = () => {
				this.isLoginLinkSubmit = false;
				this.loginLinkError = true;
				this.loginLinkSuccess = false;
			};
			userApi.generateLinkLogin(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			pending: 'pending',
			loginError: 'loginError',
		}),
		action() {
			const { action } = this.$route.query;
			return action;
		},
	},
	created() {
		document.body.classList.add('login');
		if (this.action && this.action === 'forgot_password') {
			this.isShowResetPassword = true;
		}
	},
	destroyed() {
		document.body.classList.remove('login');
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
	@keyframes bounce {
		0%, 20%, 60%, 100% {
			-webkit-transform: translateY(0);
			transform: translateY(0);
		}

		40% {
			-webkit-transform: translateY(-10px);
			transform: translateY(-10px);
		}

		80% {
			-webkit-transform: translateY(-5px);
			transform: translateY(-5px);
		}
	}

	.page-login {
		padding-top: 20vh;
		text-align: center;
		margin-bottom: 2em;

	.social-button {
		width: 40px;
		background: #fff;
		padding: 10px;
		border-radius: 100%;
		box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
		margin-right: 5px;
		margin-left: 5px;
		outline: 0;
		border: 0;
	}

	.social-button:hover {
		animation: bounce 1s;
	}

	.btn-submit {
		padding-top: .5em;
		padding-bottom: .5em;
		// font-weight: bold;
		text-transform: uppercase;
		min-width: 150px;
	}

	.login-box {
		margin: 0 auto;
		max-width: 750px;
		background-color: #fff;
		border-radius: 3px;
		// border: 1px solid #d7d7da;
		box-shadow: 0 0 10px 5px rgba(195, 195, 195, 0.3);
		overflow: hidden;
	}

	.login-box-image {
		min-width: 250px;
		// width: 250px;
	}

	.login-box-content {
		padding: .5em 2.5em 1em;
		width: 100%;
	}

	.login-box-logo {
		margin: 0 auto;
		margin-bottom: 2em;
		max-width: 210px;

		img {
			max-width: 100%;
			height: auto;
		}
	}

	.login-error {
		margin-bottom: 2em;
		font-size: .95em;
	}

	.login-action {
		display: inline-block;
		// margin-top: 1em;
		// color: #75cfd8 !important;
		cursor: pointer;

		&:hover {
			text-decoration: underline !important;
		}
	}

	.line-divider {
		position: relative;
		top: 12px;
		height: 1px;
		background-color: #e6ebeb;
	}

	.line-title {
		width: 120px;
		background-color: #FFF;
		z-index: 12;
		margin: 0 auto;
		position: relative;
	}
}
</style>
